import { EmptyState } from '@restomarker/ui-kit';
import { Link } from '@tanstack/react-router';
import type { ReactElement } from 'react';

import { startScreenFor } from '@/modules/auth';
import { t } from '@/shared/i18n';
import { useSession } from '@/shared/session';

import styles from './forbiddenPage.module.css';

/**
 * Несуществующий адрес.
 *
 * До этого роутер отдавал голое «Not Found» без единого элемента интерфейса: ни
 * навигации, ни кнопки, ни объяснения. Заметно это стало, когда удалили раздел
 * устройств, — у того, кто держал его в закладках, приложение просто обрывалось
 * посреди белого экрана.
 *
 * Действие обязательно, по той же причине, что и на экране отказа: в оболочке
 * на устройстве точки адресной строки нет вовсе, и без кнопки человек заперт
 * до перезапуска приложения. Цель считается по роли — «главная» у владельца сети
 * и у повара разная.
 *
 * Раскладку берём у экрана отказа: это одна и та же ситуация с точки зрения
 * человека («сюда нельзя»), и разводить два похожих экрана значит завести два
 * места, где потом поправят по-разному.
 */
export function NotFoundPage(): ReactElement {
  const session = useSession();

  return (
    <main data-testid="not-found" className={styles.page}>
      <EmptyState
        title={t('auth.notFound.title')}
        description={t('auth.notFound.description')}
        action={
          session.data ? (
            <Link to={startScreenFor(session.data)} className={styles.action}>
              {t('auth.forbidden.action')}
            </Link>
          ) : null
        }
      />
    </main>
  );
}
